import { spaces } from '@kimuno/theme';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Collapse,
	Divider,
	Grow,
	type Theme,
	Typography,
	useMediaQuery,
} from '@mui/material';
import _ from 'lodash';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { filterChipStyle, NONE_OF_THE_ABOVE_OPTION } from '../../../constants';
import getMuiIcon from '../../../utils/getMuiIcon';
import { smoothScrollIntoView } from '../../../utils/scroll';
import useFilterChipMaxHeight from '../../../utils/useFilterChipMaxHeight';
import FeatureStatusChip from '../../cards/MuiCard/MuiCardBase/FeatureStatusChip';
import FilterChip from './FilterChip';
import { SCROLL_DELAY } from './SearchFiltersDialog';

type FilterOption = {
	name: string;
	value: string;
	isSelected?: boolean;
};

export interface SearchFiltersAccordionProps {
	title: string;
	options: FilterOption[];
	isExpanded: boolean;
	isDisabled?: boolean;
	isNew?: boolean;
	toggleAccordion: () => void;
	truncateOptions?: {
		numberOfLines: number;
	};
	selectOption: (option: FilterOption['value']) => void;
	alert?: {
		message: string;
	};
	onLoad?: () => void;
}

const SearchFiltersAccordion = forwardRef<HTMLDivElement, SearchFiltersAccordionProps>(
	(props, ref) => {
		const {
			title,
			options,
			isExpanded,
			isDisabled,
			isNew,
			toggleAccordion,
			truncateOptions,
			selectOption,
			onLoad,
			alert,
		} = props;
		const [areOptionsTruncated, setAreOptionsTruncated] = useState(!!truncateOptions);
		const { t } = useTranslation();
		const filteredOptions = _.uniqBy(options, 'value').filter(
			(option) => option.value !== NONE_OF_THE_ABOVE_OPTION
		);
		const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('mobile'));
		const maxHeight = useFilterChipMaxHeight({ chipStyle: filterChipStyle });

		const [showAlert, setShowAlert] = useState(false);
		const [hovering, setHovering] = useState(false);
		const hoverDuration = 1000;

		useEffect(() => {
			let timer: ReturnType<typeof setTimeout> | undefined;
			if (hovering) {
				timer = setTimeout(() => {
					setShowAlert(true);
				}, hoverDuration);
			} else {
				setShowAlert(false);
				if (timer) {
					clearTimeout(timer);
				}
			}

			return () => clearTimeout(timer);
		}, [hovering]);

		// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
		useEffect(() => {
			if (options.length > 0) {
				onLoad && onLoad();
			}
		}, [options]);

		const numberOfSelectedOptions = filteredOptions.filter((option) => option.isSelected).length;

		const handleShowAllClick = () => {
			setAreOptionsTruncated(!areOptionsTruncated);
			scrollToTitle();
		};

		const scrollToTitle = () => {
			if (ref && 'current' in ref && ref.current) {
				setTimeout(() => ref.current && smoothScrollIntoView(ref.current), SCROLL_DELAY);
			}
		};

		return (
			<Box
				sx={{
					'&:last-of-type .accordion-divider': {
						display: 'none',
					},
					position: 'relative',
				}}
				ref={ref}
				onMouseEnter={() => setHovering(true)}
				onMouseLeave={() => setHovering(false)}
				onTouchStart={() => {
					setShowAlert(true);
					setTimeout(() => setShowAlert(false), 2000);
				}}
			>
				{alert && isDisabled && (
					<Grow in={showAlert}>
						<Alert
							sx={{
								position: 'absolute',
								top: spaces.xs,
								bottom: spaces.xs,
								left: spaces.sm,
								...(isMobile && { right: spaces.sm }),
								display: 'flex',
								alignItems: 'center',
								borderRadius: spaces.sm,
								zIndex: 1,
							}}
							severity="info"
							variant="standard"
						>
							{alert.message}
						</Alert>
					</Grow>
				)}
				<Accordion
					disabled={isDisabled || !filteredOptions.length}
					onChange={() => {
						toggleAccordion();
						// this is a workaround to scroll to the title when the accordion is expanded
						if (!isExpanded) {
							setTimeout(scrollToTitle, SCROLL_DELAY);
						}
					}}
					expanded={isExpanded && !isDisabled && isExpanded && filteredOptions.length !== 0}
					data-testid={`filters-accordion-${title}`}
					sx={{
						border: 'none',
						paddingY: spaces.md,
						marginBottom: '0 !important',
						marginTop: '0 !important',
						transition: 'padding 0.3s ease',
						'&.Mui-disabled': {
							backgroundColor: 'transparent',
						},
						'&.Mui-expanded': {
							paddingBottom: spaces.xs,
						},
						'::before': {
							display: 'none',
						},
					}}
					elevation={0}
				>
					<AccordionSummary
						expandIcon={<Box component={getMuiIcon('arrowDown')} />}
						sx={{
							paddingY: spaces.xs,
							minHeight: '40px !important',
							'& .MuiAccordionSummary-content': {
								margin: '0 !important',
							},
						}}
					>
						<Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: spaces.xs }}>
							{title + (numberOfSelectedOptions > 0 ? ` ∙ ${numberOfSelectedOptions}` : '')}
							{isNew && (
								<FeatureStatusChip size="small" sx={{ position: 'static', top: 'unset', left: 'unset' }} />
							)}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.xs,
							}}
						>
							<Collapse in={!areOptionsTruncated} collapsedSize={maxHeight}>
								<Box
									sx={{
										display: 'flex',
										gap: `${filterChipStyle.gap}px`,
										flexWrap: 'wrap',
										overflow: 'hidden',
									}}
								>
									{filteredOptions.map((option) => (
										<div key={option.value} data-testid={`filter-chip-${option.value}`}>
											<FilterChip
												isSelected={option.isSelected || false}
												value={option.value}
												label={option.name}
												onClick={() => selectOption(option.value)}
											/>
										</div>
									))}
								</Box>
							</Collapse>
							{!!truncateOptions && (
								<Box>
									<Button
										color="primary"
										size="medium"
										onClick={handleShowAllClick}
										sx={{
											marginLeft: `-${spaces.xs}`,
										}}
									>
										{areOptionsTruncated ? t('Show all <total>', { total: options.length }) : t('Show less')}
									</Button>
								</Box>
							)}
						</Box>
					</AccordionDetails>
				</Accordion>
				<Box
					className="accordion-divider"
					sx={{
						display: 'flex',
						paddingY: 0,
						paddingX: spaces.md,
						flexDirection: 'column',
						alignSelf: 'stretch',
					}}
				>
					<Divider />
				</Box>
			</Box>
		);
	}
);

export { SearchFiltersAccordion, type FilterOption };
